import React from 'react';
import PropTypes from 'prop-types';
import {
  withFormik,
  Form,
  FormikBag,
  FormikProps,
  FieldArray,
  FieldArrayRenderProps,
} from 'formik';
import {
  FormGroup,
  Label,
  Input,
  Card,
  CardBody,
  CardHeader,
  Spinner,
  Alert,
  FormFeedback,
} from 'reactstrap';

import i18next from 'i18next';
import { CustomRule } from 'types';
import Shapes from 'shapes/main';
import { TimePicker } from 'app/components/common/TimePicker';
import OtoButtons from 'app/components/common/OtoButtons';
import { OtoToggle } from './common';

const newRule: TFormRule = {
  key: 'visitDate',
  values: [],
  disableBook: true,
  disableOrder: true,
  text: '',
  allowedBookingHours: [],
  allowedBookingGuestsNumber: [],
};

const getTodayISO = (): string => new Date().toISOString().slice(0, 10);

const getRuleLabel = (rule: TFormRule) => {
  if (rule.disableBook && rule.disableOrder) {
    return i18next.t('closed-days-form.block-orders-and-reservations');
  }
  if (rule.disableOrder) {
    return i18next.t('closed-days-form.block-orders');
  }
  if (rule.disableBook) {
    return i18next.t('closed-days-form.block-reservations');
  }
  if (rule.allowedBookingHours.length > 0 && rule.allowedBookingGuestsNumber.length > 0) {
    return i18next.t('closed-days-form.allowed-hours-and-guests');
  }
  if (rule.allowedBookingHours.length > 0) {
    return i18next.t('closed-days-form.allowed-hours-only');
  }
  if (rule.allowedBookingGuestsNumber.length > 0) {
    return i18next.t('closed-days-form.allowed-guests-only');
  }
  return i18next.t('closed-days-form.message-only');
};

type TOwnProps = {
  hasReservations?: boolean;
  rules: CustomRule[] | null;
  /** Called with form values; parent performs API call and updates store. Form reinitializes when rules prop updates after success. */
  onSubmit: (values: ClosedDaysFormValues) => Promise<void>;
};

/** Form state: dates as array of YYYY-MM-DD; booking hours/guests as arrays (parsed from API strings) */
type TFormRule = Omit<CustomRule, 'values' | 'allowedBookingHours' | 'allowedBookingGuestsNumber'> & {
  /** Array of date strings in YYYY-MM-DD format */
  values: string[];
  /** Client-only: array for UI; API stores comma-separated string */
  allowedBookingHours: string[];
  /** Client-only: array for UI; API stores comma-separated string */
  allowedBookingGuestsNumber: string[];
};

interface ClosedDaysFormValues {
  rules: TFormRule[];
}

interface RuleFieldErrors {
  values?: string;
  allowedBookingHours?: string;
  allowedBookingGuestsNumber?: string;
}

interface ClosedDaysFormErrors {
  rules?: RuleFieldErrors[];
}

type TProps = FormikProps<ClosedDaysFormValues> & TOwnProps;

class ClosedDaysForm extends React.PureComponent<TProps, {}> {
  static propTypes = {
    rules: PropTypes.arrayOf(Shapes.customRuleShape.isRequired),
  };

  render() {
    return (
      <Form>
        <FieldArray name="rules" render={this.renderRulesArray} />
      </Form>
    );
  }

  renderRulesArray = (arrayHelpers: FieldArrayRenderProps) => {
    const { errors, values, handleChange, isSubmitting, setFieldValue, hasReservations } = this.props;
    const ruleErrors = (errors as ClosedDaysFormErrors).rules;
    const rules = values.rules || [];

    const isValidRule = (rule: TFormRule) => {
      if (!rule.text) {
        return false;
      }
      if (rule.values.length === 0) {
        return false;
      }
      return true;
    };

    const hasAnyRuleWithoutDates = rules.some((rule) => rule.values.length === 0);

    return (
      <>
        {rules.map((rule, index) => (
          <Card key={index} outline color={isValidRule(rule) ? undefined : 'danger'}>
            <CardHeader className="d-flex justify-content-between align-items-center">
              <h5 className="bb-1">
                {i18next.t('closed-days-form.special-day-title', { index: index + 1 })} ({getRuleLabel(rule)})
              </h5>
              <button
                type="button"
                className="btn btn-link"
                onClick={() => arrayHelpers.remove(index)}
              >
                {i18next.t('Remove')}
              </button>
            </CardHeader>
            <CardBody>
              <FormGroup>
                <Label>{i18next.t('closed-days-form.dates-section-label')}</Label>
                {(rule.values || []).map((date, dateIndex) => (
                  <div key={`${index}-date-${dateIndex}`} className="d-flex align-items-center mb-2 gap-10">
                    <Input
                      type="date"
                      name={`rules.${index}.values.${dateIndex}`}
                      value={date}
                      onChange={(e) => {
                        const current = this.props.values.rules[index]?.values ?? [];
                        const next = [...current.slice(0, dateIndex), e.target.value, ...current.slice(dateIndex + 1)];
                        setFieldValue(`rules.${index}.values`, next);
                      }}
                      invalid={!!ruleErrors?.[index]?.values}
                      className="mw-200"
                    />
                    <OtoButtons.DeleteButton
                      onClick={() => {
                        const current = this.props.values.rules[index]?.values ?? [];
                        setFieldValue(`rules.${index}.values`, current.filter((_, i) => i !== dateIndex));
                      }}
                    />
                  </div>
                ))}
                <OtoButtons.AddButton
                  onClick={() => {
                    const current = this.props.values.rules[index]?.values ?? [];
                    setFieldValue(`rules.${index}.values`, [...current, getTodayISO()]);
                  }}
                >
                  {i18next.t('closed-days-form.add-date')}
                </OtoButtons.AddButton>
                {ruleErrors?.[index]?.values && <FormFeedback className="d-block">{ruleErrors[index].values}</FormFeedback>}
              </FormGroup>
              <FormGroup>
                <OtoToggle
                  name={`rules.${index}.disableBook`}
                  onChange={() => setFieldValue(`rules.${index}.disableBook`, !rule.disableBook)}
                  checked={rule.disableBook || false}
                  label={i18next.t('closed-days-form.disable-reservations')}
                  labelWrapperClassName="d-flex align-vertical my-2"
                />
                <OtoToggle
                  name={`rules.${index}.disableOrder`}
                  onChange={() => setFieldValue(`rules.${index}.disableOrder`, !rule.disableOrder)}
                  checked={rule.disableOrder || false}
                  label={i18next.t('closed-days-form.disable-orders')}
                  labelWrapperClassName="d-flex align-vertical my-2"
                />
              </FormGroup>
              {hasReservations && (
                <>
                  <FormGroup>
                    <Label>{i18next.t('closed-days-form.allowed-booking-hours-label')}</Label>
                    {rule.allowedBookingHours.map((time, timeIndex) => {
                      const [h, m] = time.split(':');
                      const defaultHour = (h || '12').padStart(2, '0');
                      const defaultMin = (m || '00').padStart(2, '0');
                      return (
                        <div key={`${index}-${timeIndex}-${time}`} className="d-flex align-items-center mb-2 gap-10">
                          <TimePicker
                            defaultHour={defaultHour}
                            defaultMin={defaultMin}
                            onChange={(newTime) => {
                              const current = this.props.values.rules[index]?.allowedBookingHours ?? [];
                              const next = [...current.slice(0, timeIndex), newTime, ...current.slice(timeIndex + 1)];
                              setFieldValue(`rules.${index}.allowedBookingHours`, next);
                            }}
                          />
                          <OtoButtons.DeleteButton
                            onClick={() => {
                              const current = this.props.values.rules[index]?.allowedBookingHours ?? [];
                              setFieldValue(`rules.${index}.allowedBookingHours`, current.filter((_, i) => i !== timeIndex));
                            }}
                          />
                        </div>
                      );
                    })}
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0"
                      onClick={() => {
                        const current = this.props.values.rules[index]?.allowedBookingHours ?? [];
                        setFieldValue(`rules.${index}.allowedBookingHours`, [...current, '12:00']);
                      }}
                    >
                      {i18next.t('closed-days-form.add-hour')}
                    </button>
                    {ruleErrors?.[index]?.allowedBookingHours && (
                      <FormFeedback className="d-block">{ruleErrors[index].allowedBookingHours}</FormFeedback>
                    )}
                  </FormGroup>
                  <FormGroup>
                    <Label>{i18next.t('closed-days-form.allowed-guests-number-label')}</Label>
                    {rule.allowedBookingGuestsNumber.map((guestNum, guestIndex) => (
                      <div key={`${index}-guest-${guestIndex}`} className="d-flex align-items-center mb-2">
                        <Input
                          type="number"
                          min={MIN_GUESTS}
                          max={MAX_GUESTS}
                          value={guestNum}
                          onChange={(e) => {
                            const current = this.props.values.rules[index]?.allowedBookingGuestsNumber ?? [];
                            const next = [...current.slice(0, guestIndex), e.target.value, ...current.slice(guestIndex + 1)];
                            setFieldValue(`rules.${index}.allowedBookingGuestsNumber`, next);
                          }}
                          className="mw-70 mr-2"
                        />
                        <OtoButtons.DeleteButton
                          onClick={() => {
                            const current = this.props.values.rules[index]?.allowedBookingGuestsNumber ?? [];
                            setFieldValue(`rules.${index}.allowedBookingGuestsNumber`, current.filter((_, i) => i !== guestIndex));
                          }}
                        />
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0"
                      onClick={() => {
                        const current = this.props.values.rules[index]?.allowedBookingGuestsNumber ?? [];
                        setFieldValue(`rules.${index}.allowedBookingGuestsNumber`, [...current, '2']);
                      }}
                    >
                      {i18next.t('closed-days-form.add-guest-number')}
                    </button>
                    {ruleErrors?.[index]?.allowedBookingGuestsNumber && (
                      <FormFeedback className="d-block">{ruleErrors[index].allowedBookingGuestsNumber}</FormFeedback>
                    )}
                  </FormGroup>
                </>
              )}
              <FormGroup>
                <Label for={`rules.${index}.text`}>{i18next.t('closed-days-form.message-label')}</Label>
                <Input
                  type="textarea"
                  name={`rules.${index}.text`}
                  onChange={handleChange}
                  value={rule.text || ''}
                  placeholder={i18next.t('closed-days-form.message-placeholder')}
                />
              </FormGroup>
            </CardBody>
          </Card>
        ))}
        {rules.filter((rule) => !rule.text).length > 0 && (
          <Alert color="warning">
            {i18next.t('closed-days-form.missing-message-warning')}
          </Alert>
        )}
        {hasAnyRuleWithoutDates && (
          <Alert color="danger">
            {i18next.t('closed-days-form.missing-dates-warning')}
          </Alert>
        )}
        {isSubmitting ? (
          <Spinner color="primary" />
        ) : (
          <>
            <button type="submit" disabled={hasAnyRuleWithoutDates} className="btn btn-primary">
              {i18next.t('Save')}
            </button>
            <button
              type="button"
              className="btn btn-primary ml-2"
              onClick={() => arrayHelpers.push(newRule)}
            >
              {i18next.t('closed-days-form.add-new')}
            </button>
          </>
        )}
      </>
    );
  };
}

/** Parse API string (comma-separated) to form array */
function parseApiStringToArray(apiValue: string | null | undefined): string[] {
  if (apiValue == null) return [];
  const s = typeof apiValue === 'string' ? apiValue : String(apiValue);
  return s
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

/** Convert API rule to form rule (values as string[], hours/guests as arrays) */
function apiRuleToFormRule(rule: CustomRule): TFormRule {
  const valuesArray = Array.isArray(rule.values)
    ? rule.values
    : rule.values != null && rule.values !== ''
      ? String(rule.values).split(',').map((s) => s.trim()).filter(Boolean)
      : [];
  return {
    ...rule,
    values: valuesArray,
    allowedBookingHours: parseApiStringToArray(rule.allowedBookingHours),
    allowedBookingGuestsNumber: parseApiStringToArray(rule.allowedBookingGuestsNumber),
  };
}

const mapPropsToValues = (props: TOwnProps): ClosedDaysFormValues => ({
  rules: (props.rules || []).map(apiRuleToFormRule),
});

const MIN_GUESTS = 1;
const MAX_GUESTS = 99;

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const validate = (values: ClosedDaysFormValues): ClosedDaysFormErrors => {
  const errors: ClosedDaysFormErrors = {
    rules: values.rules.map((rule): RuleFieldErrors => {
      const ruleErrors: RuleFieldErrors = {};
      if (rule.key === 'visitDate') {
        const dates = rule.values ?? [];
        const invalidDate = dates.find((d) => !DATE_REGEX.test(String(d).trim()));
        if (invalidDate !== undefined) {
          ruleErrors.values = i18next.t('closed-days-form.dates-invalid-format');
        }
      }
      return ruleErrors;
    }),
  };
  if (errors.rules?.every((r) => !r.values && !r.allowedBookingHours && !r.allowedBookingGuestsNumber)) {
    delete errors.rules;
  }
  return errors;
};

/** Convert form rule (arrays) to API rule (strings for hours/guests) */
function formRuleToApiRule(rule: TFormRule): CustomRule {
  const valuesArray = typeof rule.values as unknown === 'string' ? rule.values.filter(Boolean) : rule.values;
  return {
    ...rule,
    values: valuesArray,
    allowedBookingHours: rule.allowedBookingHours.length > 0 ? rule.allowedBookingHours.join(',') : undefined,
    allowedBookingGuestsNumber: rule.allowedBookingGuestsNumber.length > 0 ? rule.allowedBookingGuestsNumber.join(',') : undefined,
  };
}

const handleSubmit = (
  values: ClosedDaysFormValues,
  { setSubmitting, props }: FormikBag<TOwnProps, ClosedDaysFormValues>
) => {
  props
    .onSubmit(values)
    .then(() => setSubmitting(false))
    .catch(() => setSubmitting(false));
};

export type { ClosedDaysFormValues, TFormRule };
export { formRuleToApiRule, apiRuleToFormRule };
export { ClosedDaysForm };
export default withFormik<TOwnProps, ClosedDaysFormValues>({
  mapPropsToValues,
  validate,
  handleSubmit,
  enableReinitialize: true,
  displayName: 'ClosedDaysForm',
})(ClosedDaysForm);
